import config from '../config.json';

const DB_API = config.dbApi;
const TOKEN = config.dbToken;

async function doFetch(url: URL, token: string = TOKEN) {
  const res = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
  return res.json();
}

async function doMutate(
  url: URL,
  method: 'POST' | 'PATCH' | 'DELETE',
  body?: object,
  extraHeaders: Record<string, string> = {}
) {
  const res = await fetch(url, {
    method,
    headers: {
      Authorization: `Bearer ${TOKEN}`,
      'Content-Type': 'application/json',
      'Prefer': 'return=representation',
      ...extraHeaders,
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
  if (res.status === 204) return null;
  return res.json();
}

export async function getData(
  dbTable: string,
  timeField: string,
  timeFrom: string,
  timeTo: string,
  meas: string,
  osmName?: string
) {
  const url = new URL(`${DB_API}/${dbTable}`);
  url.searchParams.set(timeField, `lt.${timeTo}`);
  url.searchParams.append(timeField, `gt.${timeFrom}`);
  url.searchParams.set('name', `eq.${meas}`);
  if (osmName) url.searchParams.set('osm_name', `eq.${osmName}`);
  url.searchParams.set('order', timeField);
  return doFetch(url);
}

export type DashboardConfig = {
  id: number;
  unit_id: string;
  serial_number: string;
  osm_name: string;
  description: string | null;
  gas_channel: 'CNT1' | 'CNT2' | null;
  gas_pulse_div: number | null;
  water_channel: 'CNT1' | 'CNT2' | null;
  water_pulse_div: number | null;
};

export async function updateDashboardConfig(id: number, changes: Partial<Omit<DashboardConfig, 'id'>>): Promise<DashboardConfig> {
  const url = new URL(`${DB_API}/dashboard_config`);
  url.searchParams.set('id', `eq.${id}`);
  const result = await doMutate(url, 'PATCH', changes);
  return result[0];
}

export async function getOsmNames(): Promise<DashboardConfig[]> {
  const url = new URL(`${DB_API}/dashboard_config`);
  url.searchParams.set('select', 'id,unit_id,serial_number,osm_name,description,gas_channel,gas_pulse_div,water_channel,water_pulse_div');
  return doFetch(url);
}

export async function getMeasurements() {
  const url = new URL(`${DB_API}/measurement`);
  url.searchParams.set('select', 'id,name');
  url.searchParams.append('name', 'not.like.*_min');
  url.searchParams.append('name', 'not.like.*_max');
  return doFetch(url, TOKEN);
}

export async function getOsmLocations() {
  const url = new URL(`${DB_API}/osm`);
  url.searchParams.set('select', 'id,name');
  return doFetch(url, TOKEN);
}


// Alerts

export async function getAlerts() {
  const url = new URL(`${DB_API}/alerts`);
  url.searchParams.set('select', 'id,name,measurement_id,osm_ids,operator,threshold,duration_minutes');
  return doFetch(url, TOKEN);
}

export async function createAlert(alert: {
  name: string;
  measurement_id: number;
  osm_ids: number[] | null;
  operator: string;
  threshold: number;
  duration_minutes: number;
  is_active: boolean;
  silenced_until: string | null;
}) {
  const url = new URL(`${DB_API}/alerts`);
  return doMutate(url, 'POST', alert);
}

export async function updateAlert(id: number, changes: Partial<{
  name: string;
  measurement_id: number;
  osm_ids: number[] | null;
  operator: string;
  threshold: number;
  duration_minutes: number;
  is_active: boolean;
  silenced_until: string | null;
}>) {
  const url = new URL(`${DB_API}/alerts`);
  url.searchParams.set('id', `eq.${id}`);
  return doMutate(url, 'PATCH', changes);
}

export async function deleteAlert(id: number) {
  const url = new URL(`${DB_API}/alerts`);
  url.searchParams.set('id', `eq.${id}`);
  return doMutate(url, 'DELETE');
}

export async function getAlertStatuses() {
  const url = new URL(`${DB_API}/alerts`);
  url.searchParams.set('select', 'id,name,operator,threshold,duration_minutes,is_active,silenced_until,osm_ids,measurement(name),alert_state(breaching_since,last_notified)');  return doFetch(url, TOKEN);
}

export async function silenceAlert(id: number, silencedUntil: string | null, isActive: boolean) {
  const url = new URL(`${DB_API}/alerts`);
  url.searchParams.set('id', `eq.${id}`);
  return doMutate(url, 'PATCH', { silenced_until: silencedUntil, is_active: isActive });
}


// Contact points

export async function getContactPoints() {
  const url = new URL(`${DB_API}/contact_points`);
  url.searchParams.set('select', 'id,email,notify_every_hours');
  return doFetch(url, TOKEN);
}

export async function createContactPoint(cp: { email: string; notify_every_hours: number }) {
  const url = new URL(`${DB_API}/contact_points`);
  return doMutate(url, 'POST', cp);
}

export async function updateContactPoint(id: number, changes: Partial<{ email: string; notify_every_hours: number }>) {
  const url = new URL(`${DB_API}/contact_points`);
  url.searchParams.set('id', `eq.${id}`);
  return doMutate(url, 'PATCH', changes);
}

export async function deleteContactPoint(id: number) {
  const url = new URL(`${DB_API}/contact_points`);
  url.searchParams.set('id', `eq.${id}`);
  return doMutate(url, 'DELETE');
}


// Tariffs

type Tariff = {
  id?: number;
  rate: number;
  weekday: number;
  valid_from: string;
  valid_to: string | null;
  tag: string;
  start_hour: number;
  end_hour: number;
};

export async function getTariffs() {
  const url = new URL(`${DB_API}/tariffs`);
  url.searchParams.set('select', 'id,rate,weekday,valid_from,valid_to,tag,start_hour,end_hour');
  url.searchParams.set('order', 'id');
  return doFetch(url, TOKEN);
}

export async function createTariff(tariff: Omit<Tariff, 'id'>) {
  const url = new URL(`${DB_API}/tariffs`);
  return doMutate(url, 'POST', tariff);
}

export async function updateTariff(id: number, changes: Partial<Omit<Tariff, 'id'>>) {
  const url = new URL(`${DB_API}/tariffs`);
  url.searchParams.set('id', `eq.${id}`);
  return doMutate(url, 'PATCH', changes);
}

export async function deleteTariff(id: number) {
  const url = new URL(`${DB_API}/tariffs`);
  url.searchParams.set('id', `eq.${id}`);
  return doMutate(url, 'DELETE');
}


// Dashboard Widgets

export type DashboardWidget = {
  id?: number;
  osm_name: string;
  measurement: string;
  chart_type: 'gauge' | 'line' | 'bar';
  title: string | null;
  colour: string | null;
  min_value: number | null;
  max_value: number | null;
  layout_order: number;
};

export async function getDashboardWidgets(osmName: string): Promise<DashboardWidget[]> {
  const url = new URL(`${DB_API}/dashboard_widgets`);
  url.searchParams.set('osm_name', `eq.${osmName}`);
  url.searchParams.set('order', 'layout_order');
  url.searchParams.set('select', 'id,osm_name,chart_type,title,colour,min_value,max_value,layout_order,widget_type_id,config');
  return doFetch(url, TOKEN);
}

export async function createDashboardWidget(widget: Omit<DashboardWidget, 'id'>): Promise<DashboardWidget> {
  const url = new URL(`${DB_API}/dashboard_widgets`);
  const result = await doMutate(url, 'POST', widget);
  return result[0];
}

export async function updateDashboardWidget(id: number, changes: Partial<Omit<DashboardWidget, 'id'>>): Promise<DashboardWidget> {
  const url = new URL(`${DB_API}/dashboard_widgets`);
  url.searchParams.set('id', `eq.${id}`);
  const result = await doMutate(url, 'PATCH', changes);
  return result[0];
}

export async function deleteDashboardWidget(id: number): Promise<void> {
  const url = new URL(`${DB_API}/dashboard_widgets`);
  url.searchParams.set('id', `eq.${id}`);
  await doMutate(url, 'DELETE');
}

export async function reorderDashboardWidgets(widgets: Pick<DashboardWidget, 'id' | 'layout_order'>[]): Promise<void> {
  await Promise.all(
    widgets.map(({ id, layout_order }) => updateDashboardWidget(id!, { layout_order }))
  );
}


// Dashboard Widget Calculations

export type CostDataPoint = {
  bucket:   string;
  cost_gbp: number;
};

export type ConsumptionDataPoint = {
  bucket:      string;
  consumption: number;
};

export async function getElectricityCost(
  osmName: string,
  from:    string,
  to:      string,
): Promise<CostDataPoint[]> {
  const url = new URL(`${DB_API}/rpc/get_electricity_cost`);
  return doMutate(url, 'POST', {
    p_osm_name: osmName,
    p_from:     from,
    p_to:       to,
  });
}

export async function getPulseConsumption(
  osmName:    string,
  from:       string,
  to:         string,
  channel:    'CNT1' | 'CNT2',
  pulseDiv:  number = 1.0,
): Promise<ConsumptionDataPoint[]> {
  const url = new URL(`${DB_API}/rpc/get_pulse_consumption`);
  return doMutate(url, 'POST', {
    p_osm_name:   osmName,
    p_from:       from,
    p_to:         to,
    p_channel:    channel,
    p_pulse_div: pulseDiv,
  });
}

export async function getGasCost(
  osmName:   string,
  from:      string,
  to:        string,
  channel:   'CNT1' | 'CNT2',
  pulseDiv:  number = 1.0,
): Promise<CostDataPoint[]> {
  const url = new URL(`${DB_API}/rpc/get_gas_cost`);
  return doMutate(url, 'POST', {
    p_osm_name:  osmName,
    p_from:      from,
    p_to:        to,
    p_channel:   channel,
    p_pulse_div: pulseDiv,
  });
}

export async function getWaterCost(
  osmName:   string,
  from:      string,
  to:        string,
  channel:   'CNT1' | 'CNT2',
  pulseDiv:  number = 1.0,
): Promise<CostDataPoint[]> {
  const url = new URL(`${DB_API}/rpc/get_water_cost`);
  return doMutate(url, 'POST', {
    p_osm_name:  osmName,
    p_from:      from,
    p_to:        to,
    p_channel:   channel,
    p_pulse_div: pulseDiv,
  });
}

export type VibrationDataPoint = {
  bucket: string;
  zrv1:   number;
  xrv1:   number;
  zpv1:   number;
  xpv1:   number;
};

export async function getVibration(
  osmName: string,
  from:    string,
  to:      string,
): Promise<VibrationDataPoint[]> {
  const url = new URL(`${DB_API}/rpc/get_vibration`);
  return doMutate(url, 'POST', {
    p_osm_name: osmName,
    p_from:     from,
    p_to:       to,
  });
}

export async function getImportCost(
  osmName: string,
  from:    string,
  to:      string,
  tag:     string = 'elec',
): Promise<CostDataPoint[]> {
  const url = new URL(`${DB_API}/rpc/get_import_cost`);
  return doMutate(url, 'POST', {
    p_osm_name: osmName,
    p_from:     from,
    p_to:       to,
    p_tag:      tag,
  });
}

export async function getImportConsumption(
  osmName: string,
  from:    string,
  to:      string,
): Promise<ConsumptionDataPoint[]> {
  const url = new URL(`${DB_API}/rpc/get_import_consumption`);
  return doMutate(url, 'POST', {
    p_osm_name: osmName,
    p_from:     from,
    p_to:       to,
  });
}

export async function getElectricityKwh(
  osmName: string,
  from:    string,
  to:      string,
): Promise<ConsumptionDataPoint[]> {
  const url = new URL(`${DB_API}/rpc/get_electricity_kwh`);
  return doMutate(url, 'POST', {
    p_osm_name: osmName,
    p_from:     from,
    p_to:       to,
  });
}

export type AirQualityDataPoint = {
  bucket: string;
  pm1:    number;
  pm4:    number;
  pm10:   number;
  pm25:   number;
};

export async function getAirQuality(
  osmName: string,
  from:    string,
  to:      string,
): Promise<AirQualityDataPoint[]> {
  const url = new URL(`${DB_API}/rpc/get_air_quality`);
  return doMutate(url, 'POST', {
    p_osm_name: osmName,
    p_from:     from,
    p_to:       to,
  });
}

export type ThreePhaseDataPoint = {
  bucket: string;
  cc1:    number;
  cc2:    number;
  cc3:    number;
};

export async function getThreePhaseCurrent(
  osmName: string,
  from:    string,
  to:      string,
): Promise<ThreePhaseDataPoint[]> {
  const url = new URL(`${DB_API}/rpc/get_three_phase_current`);
  return doMutate(url, 'POST', {
    p_osm_name: osmName,
    p_from:     from,
    p_to:       to,
  });
}
